import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

import { OverlayTemplateFolder } from '@/lomavisSharedUi/types/overlayTemplates';

export interface LomavisTemplateMoveFolderModalLabels {
  title: string;
  template: string;
  folder: string;
  noFolder: string;
  cancel: string;
  save: string;
  saving: string;
  loadMoreFolders?: string;
  loadingFolders?: string;
  retry?: string;
}

export interface LomavisTemplateMoveFolderModalProps {
  show: boolean;
  templateTitle?: string;
  folderId: string | null;
  initialFolderId: string | null;
  folders: OverlayTemplateFolder[];
  labels: LomavisTemplateMoveFolderModalLabels;
  validationMessage?: string;
  isSubmitting?: boolean;
  hasMoreFolders?: boolean;
  isLoadingMoreFolders?: boolean;
  loadMoreErrorMessage?: string | null;
  onFolderChange: (folderId: string | null) => void;
  onCancel: () => void;
  onSubmit: () => void;
  onLoadMoreFolders?: () => void;
  onRetryLoadMoreFolders?: () => void;
}

/** Organism: assigns an existing template to a folder or removes its folder. */
export const LomavisTemplateMoveFolderModal: React.FC<
  LomavisTemplateMoveFolderModalProps
> = ({
  show,
  templateTitle,
  folderId,
  initialFolderId,
  folders,
  labels,
  validationMessage,
  isSubmitting = false,
  hasMoreFolders = false,
  isLoadingMoreFolders = false,
  loadMoreErrorMessage,
  onFolderChange,
  onCancel,
  onSubmit,
  onLoadMoreFolders,
  onRetryLoadMoreFolders
}) => {
  const cannotSubmit = isSubmitting || folderId === initialFolderId;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!cannotSubmit) onSubmit();
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? () => undefined : onCancel}
      centered
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title className="fs-5 fw-bold">{labels.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-3">
          {templateTitle && (
            <div>
              <div className="text-muted fs-8 fw-semibold mb-1">
                {labels.template}
              </div>
              <div className="fw-semibold text-dark">{templateTitle}</div>
            </div>
          )}

          <Form.Group controlId="overlay-template-folder-selection">
            <Form.Label>{labels.folder}</Form.Label>
            <Form.Select
              size="sm"
              autoFocus
              value={folderId ?? ''}
              isInvalid={Boolean(validationMessage)}
              aria-invalid={Boolean(validationMessage)}
              disabled={isSubmitting}
              onChange={(event) => onFolderChange(event.target.value || null)}
            >
              <option value="">{labels.noFolder}</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </Form.Select>
            {validationMessage && (
              <Form.Control.Feedback type="invalid">
                {validationMessage}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {isLoadingMoreFolders && (
            <div
              className="d-flex align-items-center justify-content-center small text-muted"
              role="status"
            >
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
              {labels.loadingFolders ?? 'Loading folders'}
            </div>
          )}

          {!isLoadingMoreFolders && loadMoreErrorMessage && (
            <div className="d-flex align-items-center gap-2" role="alert">
              <span className="small text-danger flex-grow-1">
                {loadMoreErrorMessage}
              </span>
              {onRetryLoadMoreFolders && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline-danger"
                  onClick={onRetryLoadMoreFolders}
                >
                  {labels.retry ?? 'Retry'}
                </Button>
              )}
            </div>
          )}

          {!isLoadingMoreFolders &&
            !loadMoreErrorMessage &&
            hasMoreFolders &&
            onLoadMoreFolders && (
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                className="align-self-start"
                onClick={onLoadMoreFolders}
              >
                {labels.loadMoreFolders ?? 'Load more folders'}
              </Button>
            )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            size="sm"
            variant="white"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {labels.cancel}
          </Button>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={cannotSubmit}
          >
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
            )}
            {isSubmitting ? labels.saving : labels.save}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
